// src/pages/Home.tsx
import { Link } from 'react-router-dom';
import { ArrowRight, Palette, Users, Heart, Sparkles, ChevronRight } from 'lucide-react';

const Home = () => {
  const services = [
    {
      title: 'Slikanje po naročilu',
      description: 'Unikatne slike, prilagojene vašim željam in prostoru',
      icon: Palette
    },
    {
      title: 'Slikarske delavnice',
      description: 'Ustvarjalne delavnice za otroke in odrasle',
      icon: Users
    },
    {
      title: 'Umetniški izdelki',
      description: 'Keramika, tekstil in printi z umetniško noto',
      icon: Heart
    },
    {
      title: 'Svetovanje za notranjo opremo',
      description: 'Strokovno svetovanje za umetniško opremo prostora',
      icon: Sparkles
    }
  ];

  const upcomingEvents = [
    {
      title: 'Slikanje v naravi',
      date: '24. maj 2025',
      description: 'Ustvarjalna delavnica slikanja v čudoviti naravi'
    },
    {
      title: 'Velikonočne delavnice',
      date: 'Marec 2025',
      description: 'Posebne delavnice za praznične dni'
    },
    {
      title: 'Abstraktno slikanje',
      date: 'Vsak četrtek',
      description: 'Tedensko za odrasle in otroke'
    }
  ];

  const testimonials = [
    {
      text: "Alma je izjemna umetnica, ki z uporabo unikatnih tehnik ustvarja čudovita dela. Naša hiša je postala pravi umetniški prostor.",
      author: "Marija Kovačič",
      role: "Zbirateljica umetnosti"
    },
    {
      text: "Delavnice z Almo so bile neverjeten navdih za mojo hčerko. Sedaj rada slika in ustvarja svoja dela.",
      author: "Petra Novak",
      role: "Mama udeleženke delavnic"
    },
    {
      text: "Svetovanje za notranjo opremo je preseglo moja pričakovanja. Alma ima izjemen občutek za prostor in barve.",
      author: "Tomaž Zupan",
      role: "Oblikovalec interierjev"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative flex items-center justify-center overflow-hidden hero-bg" style={{ height: 'calc(100vh - 6rem)' }}>
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        ></div>
        
        <div className="container mx-auto px-6 lg:px-12 text-center relative z-10">
          <div className="glass-card p-8 md:p-12 rounded-3xl max-w-4xl mx-auto animate-fade-in-up">
            <h1 
              className="text-4xl md:text-6xl lg:text-7xl font-lora font-bold mb-6 md:mb-8 bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
              style={{ 
                lineHeight: 1.2,
                paddingBottom: '0.1em',
                marginBottom: '1.5rem'
              }}
            >
              Dobrodošli v Allmaverse
            </h1>
            <p className="text-lg md:text-xl lg:text-2xl text-white/80 mb-6 md:mb-8 leading-relaxed">
              Unikatna umetnost, oblikovanje in ustvarjanje iz srca Maribora
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/galerija"
                className="glass-card-hover px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-medium text-white inline-flex items-center justify-center space-x-2 group"
              >
                <span>Razišči galerijo</span>
                <ArrowRight className="w-4 md:w-5 h-4 md:h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/kontakt"
                className="glass-card px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-medium text-white hover:bg-white/20 transition-all duration-300 border-2 border-purple-400/50 inline-flex items-center justify-center space-x-2"
              >
                <span>Rezerviraj termin</span>
                <ChevronRight className="w-4 md:w-5 h-4 md:h-5" />
              </Link>
            </div>
          </div>
        </div>

        {/* Floating art elements */}
        <div className="absolute top-20 left-10 w-20 h-20 rounded-full bg-linear-to-br from-purple-400/20 to-pink-400/20 animate-float"></div>
        <div className="absolute bottom-32 right-16 w-32 h-32 rounded-full bg-linear-to-br from-blue-400/20 to-purple-400/20 animate-float" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/3 right-20 w-16 h-16 rounded-full bg-linear-to-br from-pink-400/20 to-purple-400/20 animate-float" style={{ animationDelay: '2s' }}></div>
      </section>

      {/* Content Sections with Gradient Background */}
      <div className="bg-linear-to-b from-slate-900/80 via-purple-900/60 to-slate-800/40">
        
        {/* About Preview */}
        <section className="py-16">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="glass-card p-8 rounded-3xl h-96 bg-linear-to-br from-purple-400/10 to-pink-400/10 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-linear-to-br from-purple-400/30 to-pink-400/30 flex items-center justify-center">
                      <Palette className="w-16 h-16 text-white" />
                    </div>
                    <p className="text-white/60 italic">Alma v svojem ateljeju</p>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <h2 
                  className="text-4xl md:text-5xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
                  style={{ 
                    lineHeight: 1.2,
                    paddingBottom: '0.1em',
                    marginBottom: '1.5rem'
                  }}
                >
                  O Alma Lovrenčič
                </h2>
                <p className="text-lg text-white/80 leading-relaxed">
                  Allmaverse je rezultat umetničke poti, ki se je začela v Benetkah in 
                  nadaljevala v Mariboru. Verjamem, da umetnost spodbuja domišljijo, 
                  inovacijo in bolj pristno življenje.
                </p>
                <blockquote className="border-l-4 border-purple-400 pl-6 py-4 italic text-white/70 text-lg">
                  "Umetnost ni to, kar vidiš, ampak to, kar drugim pomagaš videti."
                </blockquote>
                <Link
                  to="/o-nas"
                  className="inline-flex items-center space-x-2 text-purple-300 hover:text-purple-200 transition-colors group"
                >
                  <span className="font-medium">Preberi več o meni</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Services Preview */}
        <section className="py-16">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <h2 
                className="text-4xl md:text-5xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
                style={{ 
                  lineHeight: 1.2,
                  paddingBottom: '0.1em',
                  marginBottom: '1.5rem'
                }}
              >
                Moje storitve
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                Od unikatnih slik do ustvarjalnih delavnic - odkrijte svet umetnosti
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <div 
                    key={service.title}
                    className="glass-card-hover p-6 text-center group"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-linear-to-br from-purple-400/30 to-pink-400/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-3">
                      {service.title}
                    </h3>
                    <p className="text-white/70 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/storitev"
                className="glass-card-hover px-8 py-4 text-lg font-medium text-white inline-flex items-center space-x-2 group"
              >
                <span>Oglej si vse storitve</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>

        {/* Upcoming Events */}
        <section className="py-16">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <h2 
                className="text-4xl md:text-5xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
                style={{ 
                  lineHeight: 1.2,
                  paddingBottom: '0.1em',
                  marginBottom: '1.5rem'
                }}
              >
                Prihajajoči dogodki
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                Pridružite se nam na ustvarjalnih delavnicah in dogodkih
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {upcomingEvents.map((event, index) => (
                <div 
                  key={event.title}
                  className="glass-card-hover p-6 group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 bg-purple-400/20 text-purple-300 text-sm rounded-full font-medium">
                      {event.date}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {event.title}
                  </h3>
                  <p className="text-white/70 leading-relaxed">
                    {event.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/kontakt"
                className="glass-card-hover px-8 py-4 text-lg font-medium text-white inline-flex items-center space-x-2 group"
              >
                <span>Poglej vse delavnice</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <h2 
                className="text-4xl md:text-5xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
                style={{ 
                  lineHeight: 1.2,
                  paddingBottom: '0.1em',
                  marginBottom: '1.5rem'
                }}
              >
                Kaj pravijo stranke
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                Mnenja zadovoljnih strank, ki so zaupale Allmaverse za svojo umetniško pot
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={testimonial.author}
                  className="glass-card p-8 text-center group hover:bg-white/15 transition-all duration-300 rounded-3xl"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <blockquote className="text-lg text-white/90 leading-relaxed mb-6 italic">
                    "{testimonial.text}"
                  </blockquote>
                  <div className="space-y-1">
                    <cite className="text-purple-300 font-semibold not-italic">
                      {testimonial.author}
                    </cite>
                    <p className="text-white/60 text-sm">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial Quote */}
        <section className="py-16">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="max-w-4xl mx-auto text-center">
              <div className="glass-card p-12 rounded-3xl">
                <blockquote className="text-2xl md:text-3xl font-lora italic text-white/90 leading-relaxed mb-8">
                  "Verjamem, da umetnost spodbuja domišljijo, inovacijo in bolj pristno življenje."
                </blockquote>
                <cite 
                  className="text-lg font-medium bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
                  style={{ 
                    lineHeight: 1.2,
                    paddingBottom: 0,
                    marginBottom: 0
                  }}
                >
                  — Alma Lovrenčič, ustanoviteljica Allmaverse
                </cite>
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="py-16">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="max-w-2xl mx-auto text-center">
              <div className="glass-card p-8 rounded-3xl">
                <h2 
                  className="text-3xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
                  style={{ 
                    lineHeight: 1.2,
                    paddingBottom: '0.1em',
                    marginBottom: '1.5rem'
                  }}
                >
                  Ostanite v stiku
                </h2>
                <p className="text-white/80 mb-8">
                  Prijavite se na e-novice in prvi izvedite za nove delavnice, razstave in umetniške projekte.
                </p>
                <form className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    placeholder="Vaš e-naslov"
                    className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-hidden focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                    required
                  />
                  <button
                    type="submit"
                    className="glass-card-hover px-6 py-3 font-medium text-white whitespace-nowrap"
                  >
                    Prijavi se
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default Home;